import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronsLeftRight,
  ChevronsRightLeft,
  ChevronsUpDown,
  Columns3,
  Copy,
  Eye,
  EyeOff,
  ListFilter,
  Lock,
  LockOpen,
  MoreVertical,
  Pencil,
  Plus,
  RotateCcw,
  Save,
  Search,
  Table2,
  Trash2,
  X,
} from 'lucide-react'
import { useDeferredValue, useEffect, useMemo, useRef, useState, type FormEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react'
import { Pagination } from './Pagination'

export type WorkbenchValue = string | number | boolean | null | undefined
export type WorkbenchFilterType = 'text' | 'number' | 'date' | 'presence'

export interface WorkbenchColumn<Row, ColumnId extends string = string> {
  id: ColumnId
  label: string
  width: number
  contentWidth?: number
  locked?: boolean
  filterable?: boolean
  sortable?: boolean
  filterType?: WorkbenchFilterType
  getValue: (row: Row) => WorkbenchValue
  render: (row: Row, context: { rowNumber: number }) => ReactNode
  summaryFormat?: (value: number) => string
}

type FilterOperator = 'contains' | 'notContains' | 'equals' | 'notEquals' | 'greaterThan' | 'lessThan' | 'before' | 'after' | 'isEmpty' | 'isNotEmpty'
interface FilterCondition { id: number; field: string; operator: FilterOperator; value: string }
interface SortRule { field: string; direction: 'asc' | 'desc' }
interface ViewConfig {
  search: string
  filters: Array<Omit<FilterCondition, 'id'>>
  sort: SortRule | null
  hiddenColumns: string[]
  columnWidths: Record<string, number>
  columnOrder: string[]
}
interface SavedView { id: string; name: string; config: ViewConfig; locked?: boolean }
type SummaryMode = 'none' | 'count' | 'sum'
type SummaryModes = Record<string, SummaryMode>

const FILTER_OPERATORS: Record<WorkbenchFilterType, Array<{ value: FilterOperator; label: string }>> = {
  text: [
    { value: 'contains', label: '包含' }, { value: 'notContains', label: '不包含' },
    { value: 'equals', label: '等于' }, { value: 'notEquals', label: '不等于' },
    { value: 'isEmpty', label: '为空' }, { value: 'isNotEmpty', label: '不为空' },
  ],
  number: [
    { value: 'equals', label: '等于' }, { value: 'notEquals', label: '不等于' },
    { value: 'greaterThan', label: '大于' }, { value: 'lessThan', label: '小于' },
    { value: 'isEmpty', label: '为空' }, { value: 'isNotEmpty', label: '不为空' },
  ],
  date: [
    { value: 'equals', label: '等于' }, { value: 'before', label: '早于' }, { value: 'after', label: '晚于' },
    { value: 'isEmpty', label: '为空' }, { value: 'isNotEmpty', label: '不为空' },
  ],
  presence: [{ value: 'isEmpty', label: '为空' }, { value: 'isNotEmpty', label: '不为空' }],
}

const EMPTY_VIEW: ViewConfig = { search: '', filters: [], sort: null, hiddenColumns: [], columnWidths: {}, columnOrder: [] }
const filterNeedsValue = (operator: FilterOperator) => operator !== 'isEmpty' && operator !== 'isNotEmpty'
const normalize = (value: WorkbenchValue) => value === null || value === undefined ? '' : String(value).trim()
const cloneView = (config: ViewConfig): ViewConfig => JSON.parse(JSON.stringify(config))
const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
const CONTINUOUS_ROW_HEIGHT = 47
const CONTINUOUS_OVERSCAN = 12

function readSavedViews(storageKey: string): SavedView[] {
  try {
    const value = JSON.parse(localStorage.getItem(`${storageKey}:views`) || '[]')
    return Array.isArray(value) ? value.filter((item) => item?.id && item?.name && item?.config) : []
  } catch { return [] }
}

function readDefaultView(storageKey: string): ViewConfig {
  try {
    const value = JSON.parse(localStorage.getItem(`${storageKey}:default`) || 'null')
    if (!value || typeof value !== 'object' || Array.isArray(value)) return cloneView(EMPTY_VIEW)
    return {
      search: typeof value.search === 'string' ? value.search : '',
      filters: Array.isArray(value.filters) ? value.filters.filter((filter: unknown) => {
        if (!filter || typeof filter !== 'object' || Array.isArray(filter)) return false
        const item = filter as Partial<FilterCondition>
        return typeof item.field === 'string' && typeof item.operator === 'string' && typeof item.value === 'string'
      }) : [],
      sort: value.sort
        && typeof value.sort === 'object'
        && !Array.isArray(value.sort)
        && typeof value.sort.field === 'string'
        && (value.sort.direction === 'asc' || value.sort.direction === 'desc')
        ? { field: value.sort.field, direction: value.sort.direction }
        : null,
      hiddenColumns: Array.isArray(value.hiddenColumns)
        ? value.hiddenColumns.filter((item: unknown): item is string => typeof item === 'string')
        : [],
      columnWidths: value.columnWidths && typeof value.columnWidths === 'object' && !Array.isArray(value.columnWidths)
        ? Object.fromEntries(Object.entries(value.columnWidths).filter((entry): entry is [string, number] => typeof entry[1] === 'number'))
        : {},
      columnOrder: Array.isArray(value.columnOrder)
        ? value.columnOrder.filter((item: unknown): item is string => typeof item === 'string')
        : [],
    }
  } catch {
    return cloneView(EMPTY_VIEW)
  }
}

function readSummaryModes(storageKey: string, defaultColumnId: string): SummaryModes {
  const defaults = defaultColumnId ? { [defaultColumnId]: 'count' as const } : {}
  try {
    const value = JSON.parse(localStorage.getItem(`${storageKey}:summary`) || '{}')
    return value && typeof value === 'object' && !Array.isArray(value) ? { ...defaults, ...value } : defaults
  } catch { return defaults }
}

function matchesFilter(value: WorkbenchValue, condition: FilterCondition) {
  const raw = normalize(value)
  if (condition.operator === 'isEmpty') return raw === ''
  if (condition.operator === 'isNotEmpty') return raw !== ''
  const expected = condition.value.trim()
  if (!expected) return true
  if (condition.operator === 'contains') return raw.toLocaleLowerCase('zh-CN').includes(expected.toLocaleLowerCase('zh-CN'))
  if (condition.operator === 'notContains') return !raw.toLocaleLowerCase('zh-CN').includes(expected.toLocaleLowerCase('zh-CN'))
  if (condition.operator === 'equals') return raw.toLocaleLowerCase('zh-CN') === expected.toLocaleLowerCase('zh-CN')
  if (condition.operator === 'notEquals') return raw.toLocaleLowerCase('zh-CN') !== expected.toLocaleLowerCase('zh-CN')
  if (condition.operator === 'greaterThan') return Number(raw) > Number(expected)
  if (condition.operator === 'lessThan') return Number(raw) < Number(expected)
  if (condition.operator === 'before') return raw < expected
  return raw > expected
}

export function DataTableWorkbench<Row, ColumnId extends string>({
  storageKey,
  columns,
  rows,
  rowKey,
  getSearchText,
  searchPlaceholder,
  emptyMessage,
  tableClassName = '',
  editAction,
  statusNote,
  initialPageSize = 50,
  continuous = false,
  summary = false,
}: {
  storageKey: string
  columns: Array<WorkbenchColumn<Row, ColumnId>>
  rows: Row[]
  rowKey: (row: Row) => string
  getSearchText?: (row: Row) => string
  searchPlaceholder: string
  emptyMessage: string
  tableClassName?: string
  editAction?: ReactNode
  statusNote?: ReactNode
  initialPageSize?: number
  continuous?: boolean
  summary?: boolean
}) {
  const savedInitial = useMemo(() => readSavedViews(storageKey), [storageKey])
  const [defaultViewConfig, setDefaultViewConfig] = useState(() => readDefaultView(storageKey))
  const initialActiveViewId = useMemo(() => {
    const stored = localStorage.getItem(`${storageKey}:active`) || 'default'
    return stored === 'default' || savedInitial.some((view) => view.id === stored) ? stored : 'default'
  }, [savedInitial, storageKey])
  const initialConfig = savedInitial.find((view) => view.id === initialActiveViewId)?.config || defaultViewConfig
  const [savedViews, setSavedViews] = useState<SavedView[]>(savedInitial)
  const [activeViewId, setActiveViewId] = useState(initialActiveViewId)
  const [search, setSearch] = useState(initialConfig.search || '')
  const [committedSearch, setCommittedSearch] = useState(initialConfig.search || '')
  const [filters, setFilters] = useState<FilterCondition[]>(() => (initialConfig.filters || []).map((filter, index) => ({ ...filter, id: Date.now() + index })))
  const [sort, setSort] = useState<SortRule | null>(initialConfig.sort || null)
  const [hiddenColumns, setHiddenColumns] = useState<Set<string>>(() => new Set(initialConfig.hiddenColumns || []))
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(initialConfig.columnWidths || {})
  const [columnOrder, setColumnOrder] = useState<string[]>(initialConfig.columnOrder || [])
  const [draggedColumnId, setDraggedColumnId] = useState('')
  const [columnDropTarget, setColumnDropTarget] = useState<{ id: string; position: 'before' | 'after' } | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [fieldPanelOpen, setFieldPanelOpen] = useState(false)
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const [openFilterOptionsId, setOpenFilterOptionsId] = useState<number | null>(null)
  const [filterValueQuery, setFilterValueQuery] = useState('')
  const [sortPanelOpen, setSortPanelOpen] = useState(false)
  const [createViewOpen, setCreateViewOpen] = useState(false)
  const [newViewName, setNewViewName] = useState('')
  const [renameViewId, setRenameViewId] = useState('')
  const [renameViewName, setRenameViewName] = useState('')
  const [deleteViewId, setDeleteViewId] = useState('')
  const [openViewMenuId, setOpenViewMenuId] = useState('')
  const [viewMessage, setViewMessage] = useState('')
  const defaultSummaryColumnId = columns.find((column) => column.id === 'productName')?.id
    || columns.find((column) => column.filterable !== false && column.id !== 'image')?.id
    || ''
  const [summaryModes, setSummaryModes] = useState<SummaryModes>(() => readSummaryModes(storageKey, defaultSummaryColumnId))
  const [tableScrollTop, setTableScrollTop] = useState(0)
  const [tableScrollLeft, setTableScrollLeft] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight)
  const [stickyToolbarHeight, setStickyToolbarHeight] = useState(0)
  const [stickyToolbarTop, setStickyToolbarTop] = useState(50)
  const [summaryDock, setSummaryDock] = useState({ visible: false, left: 0, width: 0 })
  const [horizontalDock, setHorizontalDock] = useState({ visible: false, left: 0, width: 0 })
  const resizeRef = useRef<{ id: string; startX: number; startWidth: number } | null>(null)
  const columnPointerDragRef = useRef<{ id: string; startX: number; startY: number; active: boolean } | null>(null)
  const columnDropTargetRef = useRef<{ id: string; position: 'before' | 'after' } | null>(null)
  const searchCompositionRef = useRef(false)
  const workspaceRef = useRef<HTMLDivElement>(null)
  const toolbarRef = useRef<HTMLElement>(null)
  const tableScrollRef = useRef<HTMLDivElement>(null)
  const dockedHorizontalScrollRef = useRef<HTMLDivElement>(null)
  const windowScrollFrameRef = useRef<number | null>(null)
  const fieldControlRef = useRef<HTMLDivElement>(null)
  const filterControlRef = useRef<HTMLDivElement>(null)
  const sortControlRef = useRef<HTMLDivElement>(null)
  const savedViewBarRef = useRef<HTMLElement>(null)
  const columnById = useMemo(() => new Map(columns.map((column) => [column.id, column])), [columns])
  const filterValueOptions = useMemo(() => {
    const collator = new Intl.Collator('zh-CN', { numeric: true, sensitivity: 'base' })
    const buckets = new Map<string, Set<string>>()
    for (const column of columns) {
      if (column.filterable === false) continue
      const type = column.filterType || 'text'
      if (type !== 'text') continue
      buckets.set(column.id, new Set())
    }
    if (!buckets.size) return {} as Record<string, string[]>
    for (const row of rows) {
      for (const [columnId, values] of buckets) {
        const column = columnById.get(columnId as ColumnId)
        if (!column) continue
        const raw = normalize(column.getValue(row))
        if (raw) values.add(raw)
      }
    }
    return Object.fromEntries(
      Array.from(buckets.entries()).map(([columnId, values]) => [
        columnId,
        Array.from(values).sort(collator.compare),
      ]),
    ) as Record<string, string[]>
  }, [columnById, columns, rows])
  const deferredSearch = useDeferredValue(committedSearch)
  const orderedColumns = useMemo(() => {
    const validIds = new Set<string>(columns.map((column) => column.id))
    const seen = new Set<string>()
    const requestedIds = columnOrder.filter((id) => validIds.has(id) && !seen.has(id) && seen.add(id))
    const requestedColumns = [...requestedIds, ...columns.map((column) => column.id).filter((id) => !seen.has(id))]
      .map((id) => columnById.get(id as ColumnId))
      .filter((column): column is WorkbenchColumn<Row, ColumnId> => Boolean(column))
    return [
      ...columns.filter((column) => column.locked),
      ...requestedColumns.filter((column) => !column.locked),
    ]
  }, [columnById, columnOrder, columns])
  const visibleColumns = orderedColumns.filter((column) => !hiddenColumns.has(column.id))
  const tableWidth = Math.max(900, visibleColumns.reduce((total, column) => total + (columnWidths[column.id] || column.width), 0))
  const currentConfig = useMemo<ViewConfig>(() => ({
    search,
    filters: filters.map(({ field, operator, value }) => ({ field, operator, value })),
    sort,
    hiddenColumns: Array.from(hiddenColumns).sort(),
    columnWidths,
    columnOrder,
  }), [columnOrder, columnWidths, filters, hiddenColumns, search, sort])
  const activeView = savedViews.find((view) => view.id === activeViewId)
  const activeConfig = activeView?.config || defaultViewConfig
  const comparableActiveConfig = { ...activeConfig, columnOrder: activeConfig.columnOrder || [] }
  const viewDirty = JSON.stringify(currentConfig) !== JSON.stringify(comparableActiveConfig)

  useEffect(() => { localStorage.setItem(`${storageKey}:views`, JSON.stringify(savedViews)) }, [savedViews, storageKey])
  useEffect(() => { localStorage.setItem(`${storageKey}:active`, activeViewId) }, [activeViewId, storageKey])
  useEffect(() => {
    if (summary) localStorage.setItem(`${storageKey}:summary`, JSON.stringify(summaryModes))
  }, [storageKey, summary, summaryModes])
  useEffect(() => {
    const move = (event: PointerEvent) => {
      const resize = resizeRef.current
      if (!resize) return
      setColumnWidths((current) => ({ ...current, [resize.id]: Math.max(64, Math.min(520, resize.startWidth + event.clientX - resize.startX)) }))
    }
    const up = () => { resizeRef.current = null; document.body.classList.remove('is-resizing-column') }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', up)
    return () => { document.removeEventListener('pointermove', move); document.removeEventListener('pointerup', up) }
  }, [])
  useEffect(() => {
    const toolbar = toolbarRef.current
    if (!toolbar) return
    const syncToolbarGeometry = () => {
      setStickyToolbarHeight(Math.ceil(toolbar.getBoundingClientRect().height))
      const computedTop = Number.parseFloat(window.getComputedStyle(toolbar).top)
      setStickyToolbarTop(Number.isFinite(computedTop) ? computedTop : 0)
    }
    syncToolbarGeometry()
    const observer = new ResizeObserver(syncToolbarGeometry)
    observer.observe(toolbar)
    window.addEventListener('resize', syncToolbarGeometry)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', syncToolbarGeometry)
    }
  }, [])
  useEffect(() => {
    const syncWindowScroll = () => {
      windowScrollFrameRef.current = null
      const table = tableScrollRef.current
      if (!table) return
      const tableRect = table.getBoundingClientRect()
      if (continuous) {
        const tableTop = tableRect.top + window.scrollY
        setTableScrollTop(Math.max(0, window.scrollY - tableTop))
      }
      setViewportHeight(window.innerHeight)
      const left = Math.max(0, tableRect.left)
      const right = Math.min(window.innerWidth, tableRect.right)
      const width = Math.max(0, right - left)
      const nextDock = {
        visible: continuous && summary && tableRect.top < window.innerHeight && tableRect.bottom > window.innerHeight,
        left,
        width,
      }
      setSummaryDock((current) => current.visible === nextDock.visible && current.left === nextDock.left && current.width === nextDock.width ? current : nextDock)
      const nextHorizontalDock = {
        visible: width > 0
          && table.scrollWidth > table.clientWidth + 1
          && tableRect.top < window.innerHeight
          && tableRect.bottom > window.innerHeight,
        left,
        width,
      }
      setHorizontalDock((current) => current.visible === nextHorizontalDock.visible && current.left === nextHorizontalDock.left && current.width === nextHorizontalDock.width ? current : nextHorizontalDock)
    }
    const schedule = () => {
      if (windowScrollFrameRef.current !== null) return
      windowScrollFrameRef.current = window.requestAnimationFrame(syncWindowScroll)
    }
    schedule()
    window.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule)
    const observer = new ResizeObserver(schedule)
    observer.observe(document.body)
    const sourceTable = tableScrollRef.current?.querySelector('table')
    if (tableScrollRef.current) observer.observe(tableScrollRef.current)
    if (sourceTable) observer.observe(sourceTable)
    return () => {
      window.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
      observer.disconnect()
      if (windowScrollFrameRef.current !== null) window.cancelAnimationFrame(windowScrollFrameRef.current)
    }
  }, [continuous, summary, tableWidth])

  useEffect(() => {
    if (!horizontalDock.visible) return
    const source = tableScrollRef.current
    const docked = dockedHorizontalScrollRef.current
    if (source && docked && Math.abs(docked.scrollLeft - source.scrollLeft) > 0.5) docked.scrollLeft = source.scrollLeft
  }, [horizontalDock.visible, tableWidth])

  useEffect(() => {
    if (!fieldPanelOpen && !filterPanelOpen && !sortPanelOpen) return
    const closeToolbarPanels = () => {
      setFieldPanelOpen(false)
      setFilterPanelOpen(false)
      setSortPanelOpen(false)
      setOpenFilterOptionsId(null)
      setFilterValueQuery('')
    }
    const handlePointerDown = (event: PointerEvent) => {
      const activeControl = fieldPanelOpen
        ? fieldControlRef.current
        : filterPanelOpen
          ? filterControlRef.current
          : sortControlRef.current
      if (activeControl && event.composedPath().includes(activeControl)) return
      closeToolbarPanels()
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (openFilterOptionsId !== null) {
        setOpenFilterOptionsId(null)
        setFilterValueQuery('')
        return
      }
      closeToolbarPanels()
    }
    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [fieldPanelOpen, filterPanelOpen, openFilterOptionsId, sortPanelOpen])

  useEffect(() => {
    if (!openViewMenuId) return
    const closeSavedViewMenu = (event: PointerEvent) => {
      if (savedViewBarRef.current && event.composedPath().includes(savedViewBarRef.current)) return
      setOpenViewMenuId('')
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenViewMenuId('')
    }
    document.addEventListener('pointerdown', closeSavedViewMenu)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', closeSavedViewMenu)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [openViewMenuId])

  const applyView = (config: ViewConfig) => {
    const validIds = new Set(columns.map((column) => column.id))
    setSearch(config.search || '')
    setCommittedSearch(config.search || '')
    setFilters((config.filters || []).filter((filter) => validIds.has(filter.field as ColumnId)).map((filter, index) => ({ ...filter, id: Date.now() + index })))
    setSort(config.sort && validIds.has(config.sort.field as ColumnId) ? config.sort : null)
    setHiddenColumns(new Set((config.hiddenColumns || []).filter((id) => validIds.has(id as ColumnId))))
    setColumnWidths(config.columnWidths || {})
    setColumnOrder((config.columnOrder || []).filter((id, index, ids) => validIds.has(id as ColumnId) && ids.indexOf(id) === index))
    setPage(1)
  }
  const switchView = (id: string) => {
    if (id === activeViewId) return
    if (viewDirty && !window.confirm('当前页面配置尚未保存，确定切换页面吗？')) return
    const next = savedViews.find((view) => view.id === id)
    applyView(next?.config || defaultViewConfig)
    setActiveViewId(id)
    setOpenViewMenuId('')
    setViewMessage('')
  }
  const saveActiveView = () => {
    if (activeViewId === 'default') {
      const next = cloneView(currentConfig)
      setDefaultViewConfig(next)
      localStorage.setItem(`${storageKey}:default`, JSON.stringify(next))
      setViewMessage('已保存“全部数据”。')
      return
    }
    if (activeView?.locked) { setViewMessage(`“${activeView.name}”已锁定，请先解锁视图。`); return }
    setSavedViews((current) => current.map((view) => view.id === activeViewId ? { ...view, config: cloneView(currentConfig) } : view))
    setViewMessage(`已保存“${activeView?.name}”。`)
  }
  const createSavedView = (event: FormEvent) => {
    event.preventDefault()
    const name = newViewName.trim()
    if (!name) return
    const view = { id: uid(), name, config: cloneView(currentConfig), locked: false }
    setSavedViews((current) => [...current, view])
    setActiveViewId(view.id)
    setCreateViewOpen(false)
    setNewViewName('')
    setViewMessage(`已创建“${name}”。`)
  }
  const renameSavedView = (event: FormEvent) => {
    event.preventDefault()
    const name = renameViewName.trim()
    if (!name) return
    setSavedViews((current) => current.map((view) => view.id === renameViewId ? { ...view, name } : view))
    setRenameViewId('')
    setRenameViewName('')
  }
  const duplicateView = (view: SavedView) => {
    const copy = { ...view, id: uid(), name: `${view.name} 副本`, config: cloneView(view.config), locked: false }
    setSavedViews((current) => [...current, copy])
    setOpenViewMenuId('')
  }
  const deleteView = () => {
    setSavedViews((current) => current.filter((view) => view.id !== deleteViewId))
    if (activeViewId === deleteViewId) { setActiveViewId('default'); applyView(defaultViewConfig) }
    setDeleteViewId('')
  }
  const toggleViewLock = (view: SavedView) => {
    setSavedViews((current) => current.map((item) => item.id === view.id ? { ...item, locked: !item.locked } : item))
    setOpenViewMenuId('')
    setViewMessage(view.locked ? `已解锁“${view.name}”。` : `已锁定“${view.name}”。`)
  }
  const closeFilterValueOptions = () => {
    setOpenFilterOptionsId(null)
    setFilterValueQuery('')
  }
  const toggleFilterValueOptions = (id: number) => {
    setOpenFilterOptionsId((current) => {
      setFilterValueQuery('')
      return current === id ? null : id
    })
  }
  const addFilter = () => {
    const column = columns.find((item) => item.filterable !== false) || columns[0]
    if (!column) return
    const type = column.filterType || 'text'
    closeFilterValueOptions()
    setFilters((current) => [...current, { id: Date.now(), field: column.id, operator: FILTER_OPERATORS[type][0].value, value: '' }])
  }
  const updateFilter = (id: number, patch: Partial<FilterCondition>) => setFilters((current) => current.map((filter) => filter.id === id ? { ...filter, ...patch } : filter))
  const toggleColumn = (column: WorkbenchColumn<Row, ColumnId>) => {
    if (column.locked) return
    setHiddenColumns((current) => {
      const next = new Set(current)
      if (next.has(column.id)) next.delete(column.id)
      else if (columns.length - next.size > 1) next.add(column.id)
      return next
    })
  }
  const fitColumns = (mode: 'header' | 'content') => {
    const widths: Record<string, number> = {}
    for (const column of visibleColumns) {
      const headerWidth = Math.max(72, Math.min(240, column.label.length * 14 + 34))
      if (mode === 'header') widths[column.id] = headerWidth
      else if (column.contentWidth) widths[column.id] = column.contentWidth
      else {
        const contentWidth = rows.slice(0, 120).reduce((maximum, row) => Math.max(maximum, normalize(column.getValue(row)).length * 12 + 30), headerWidth)
        widths[column.id] = Math.max(72, Math.min(520, contentWidth))
      }
    }
    setColumnWidths(widths)
  }
  const finishColumnDrag = () => {
    columnPointerDragRef.current = null
    columnDropTargetRef.current = null
    setDraggedColumnId('')
    setColumnDropTarget(null)
    document.body.classList.remove('is-dragging-column')
  }
  const scrollTableDuringColumnDrag = (clientX: number) => {
    const table = tableScrollRef.current
    if (!table) return
    const rect = table.getBoundingClientRect()
    const edge = Math.min(72, rect.width / 4)
    if (clientX < rect.left + edge) table.scrollLeft = Math.max(0, table.scrollLeft - 28)
    else if (clientX > rect.right - edge) table.scrollLeft = Math.min(table.scrollWidth - table.clientWidth, table.scrollLeft + 28)
  }
  const reorderColumn = (sourceId: string, targetId: string, position: 'before' | 'after') => {
    const sourceColumn = columnById.get(sourceId as ColumnId)
    const targetColumn = columnById.get(targetId as ColumnId)
    if (!sourceColumn || !targetColumn || sourceColumn.locked || sourceId === targetId) return
    const nextIds: string[] = orderedColumns.map((column) => column.id).filter((id) => id !== sourceId)
    const targetIndex = nextIds.indexOf(targetId)
    const safePosition = targetColumn.locked ? 'after' : position
    nextIds.splice(Math.max(0, targetIndex + (safePosition === 'after' ? 1 : 0)), 0, sourceId)
    const lockedIds: string[] = columns.filter((column) => column.locked).map((column) => column.id)
    setColumnOrder([...lockedIds, ...nextIds.filter((id) => !lockedIds.includes(id))])
  }
  const toggleHeaderSort = (field: string) => {
    const column = columnById.get(field as ColumnId)
    if (!column || column.locked || column.filterType !== 'number' || column.sortable === false) return
    setSortPanelOpen(false)
    setFieldPanelOpen(false)
    setFilterPanelOpen(false)
    setSort((current) => {
      if (!current || current.field !== field) return { field, direction: 'desc' }
      return { field, direction: current.direction === 'desc' ? 'asc' : 'desc' }
    })
  }
  const handleColumnPointerDown = (event: ReactPointerEvent<HTMLTableCellElement>, column: WorkbenchColumn<Row, ColumnId>) => {
    if (event.button !== 0 || column.locked || (event.target as Element).closest('.sku-column-resizer')) return
    event.preventDefault()
    columnPointerDragRef.current = { id: column.id, startX: event.clientX, startY: event.clientY, active: false }
    columnDropTargetRef.current = null
    setColumnDropTarget(null)
  }
  useEffect(() => {
    const move = (event: PointerEvent) => {
      const drag = columnPointerDragRef.current
      if (!drag) return
      if (!drag.active) {
        if (Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < 5) return
        drag.active = true
        setDraggedColumnId(drag.id)
        document.body.classList.add('is-dragging-column')
      }
      event.preventDefault()
      scrollTableDuringColumnDrag(event.clientX)
      const workspace = workspaceRef.current
      if (!workspace) return
      const headerById = new Map<string, HTMLTableCellElement>()
      for (const header of workspace.querySelectorAll<HTMLTableCellElement>('th[data-column-id]')) {
        const style = window.getComputedStyle(header)
        const rect = header.getBoundingClientRect()
        if (style.visibility === 'hidden' || style.display === 'none' || rect.width <= 0 || rect.height <= 0) continue
        if (!headerById.has(header.dataset.columnId || '')) headerById.set(header.dataset.columnId || '', header)
      }
      const headers = Array.from(headerById.values()).sort((left, right) => left.getBoundingClientRect().left - right.getBoundingClientRect().left)
      const pointedHeader = (document.elementFromPoint(event.clientX, event.clientY) as Element | null)?.closest<HTMLTableCellElement>('th[data-column-id]')
      const targetHeader = pointedHeader && workspace.contains(pointedHeader)
        ? pointedHeader
        : headers.find((header) => event.clientX <= header.getBoundingClientRect().right) || headers.at(-1)
      const targetId = targetHeader?.dataset.columnId || ''
      if (!targetHeader || !targetId || targetId === drag.id) {
        columnDropTargetRef.current = null
        setColumnDropTarget(null)
        return
      }
      const targetColumn = columnById.get(targetId as ColumnId)
      const rect = targetHeader.getBoundingClientRect()
      const requestedPosition: 'before' | 'after' = event.clientX < rect.left + rect.width / 2 ? 'before' : 'after'
      const nextTarget: { id: string; position: 'before' | 'after' } = { id: targetId, position: targetColumn?.locked ? 'after' : requestedPosition }
      columnDropTargetRef.current = nextTarget
      setColumnDropTarget((current) => current?.id === nextTarget.id && current.position === nextTarget.position ? current : nextTarget)
    }
    const up = (event: PointerEvent) => {
      const drag = columnPointerDragRef.current
      const target = columnDropTargetRef.current
      if (drag?.active) {
        event.preventDefault()
        if (target) reorderColumn(drag.id, target.id, target.position)
      } else if (drag) {
        const column = columnById.get(drag.id as ColumnId)
        if (column && !column.locked && column.filterType === 'number' && column.sortable !== false) {
          toggleHeaderSort(drag.id)
        }
      }
      finishColumnDrag()
    }
    document.addEventListener('pointermove', move)
    document.addEventListener('pointerup', up)
    document.addEventListener('pointercancel', up)
    return () => {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
      document.removeEventListener('pointercancel', up)
    }
  }, [columnById, columns, orderedColumns])
  const processedRows = useMemo(() => {
    const needle = deferredSearch.trim().toLocaleLowerCase('zh-CN')
    const selected = rows.filter((row) => {
      if (needle) {
        const matchesSearch = getSearchText
          ? getSearchText(row).toLocaleLowerCase('zh-CN').includes(needle)
          : columns.some((column) => normalize(column.getValue(row)).toLocaleLowerCase('zh-CN').includes(needle))
        if (!matchesSearch) return false
      }
      return filters.every((filter) => {
        const column = columnById.get(filter.field as ColumnId)
        return !column || matchesFilter(column.getValue(row), filter)
      })
    })
    if (!sort) return selected
    const column = columnById.get(sort.field as ColumnId)
    if (!column) return selected
    const collator = new Intl.Collator('zh-CN', { numeric: true, sensitivity: 'base' })
    return selected.map((row, index) => ({ row, index, value: column.getValue(row) })).sort((left, right) => {
      const leftEmpty = normalize(left.value) === ''
      const rightEmpty = normalize(right.value) === ''
      if (leftEmpty || rightEmpty) return leftEmpty === rightEmpty ? left.index - right.index : leftEmpty ? 1 : -1
      const comparison = typeof left.value === 'number' && typeof right.value === 'number'
        ? left.value - right.value
        : collator.compare(normalize(left.value), normalize(right.value))
      return comparison === 0 ? left.index - right.index : sort.direction === 'asc' ? comparison : -comparison
    }).map((entry) => entry.row)
  }, [columnById, columns, deferredSearch, filters, getSearchText, rows, sort])
  const pageCount = Math.max(1, Math.ceil(processedRows.length / pageSize))
  const currentPage = Math.min(page, pageCount)
  const pagedRows = processedRows.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  const virtualVisibleCount = Math.ceil(viewportHeight / CONTINUOUS_ROW_HEIGHT)
  const virtualScrollStart = Math.min(
    Math.floor(tableScrollTop / CONTINUOUS_ROW_HEIGHT),
    Math.max(0, processedRows.length - virtualVisibleCount),
  )
  const virtualStart = continuous ? Math.max(0, virtualScrollStart - CONTINUOUS_OVERSCAN) : 0
  const virtualEnd = continuous
    ? Math.min(processedRows.length, virtualScrollStart + virtualVisibleCount + CONTINUOUS_OVERSCAN)
    : pagedRows.length
  const renderedRows = continuous ? processedRows.slice(virtualStart, virtualEnd) : pagedRows
  const virtualTopHeight = continuous ? virtualStart * CONTINUOUS_ROW_HEIGHT : 0
  const virtualBottomHeight = continuous ? (processedRows.length - virtualEnd) * CONTINUOUS_ROW_HEIGHT : 0
  const summarySums = useMemo(() => {
    const totals: Record<string, number> = {}
    for (const column of columns) {
      if (summaryModes[column.id] !== 'sum' || column.filterType !== 'number') continue
      totals[column.id] = processedRows.reduce((total, row) => {
        const value = column.getValue(row)
        return typeof value === 'number' && Number.isFinite(value) ? total + value : total
      }, 0)
    }
    return totals
  }, [columns, processedRows, summaryModes])
  const openView = savedViews.find((view) => view.id === openViewMenuId)
  const renderSummaryCells = (surface: 'source' | 'docked') => visibleColumns.map((column) => {
    const mode = summaryModes[column.id] || 'none'
    const countLabel = processedRows.length.toLocaleString('zh-CN')
    const sum = summarySums[column.id] || 0
    const sumLabel = column.summaryFormat
      ? column.summaryFormat(sum)
      : sum.toLocaleString('zh-CN', { maximumFractionDigits: 2 })
    return <td key={column.id} className={column.id === 'rowNumber' ? 'sku-master-rownumber-cell' : ''}>
      {column.id !== 'rowNumber' && <select
        className="sku-summary-select"
        aria-label={`${column.label}底部统计`}
        tabIndex={surface === 'source' && summaryDock.visible ? -1 : 0}
        value={mode}
        onChange={(event) => setSummaryModes((current) => ({ ...current, [column.id]: event.target.value as SummaryMode }))}
      >
        <option value="none">{mode === 'none' ? '' : '不展示'}</option>
        <option value="count">记录总数{mode === 'count' ? ` · ${countLabel}` : ''}</option>
        <option value="sum" disabled={column.filterType !== 'number'}>{mode === 'sum' ? sumLabel : '求和'}</option>
      </select>}
    </td>
  })
  const renderHeaderCells = () => visibleColumns.map((column) => {
    const dropClass = columnDropTarget?.id === column.id ? ` is-column-drop-${columnDropTarget.position}` : ''
    const headerSortable = !column.locked && column.filterType === 'number' && column.sortable !== false
    const sortActive = sort?.field === column.id
    return <th
      key={column.id}
      data-column-id={column.id}
      className={`workbench-column-${column.id}${column.locked ? '' : ' is-column-draggable'}${headerSortable ? ' is-column-sortable' : ''}${draggedColumnId === column.id ? ' is-column-dragging' : ''}${dropClass}`}
      aria-grabbed={draggedColumnId === column.id}
      aria-sort={sortActive ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
      title={column.locked
        ? undefined
        : headerSortable
          ? '点击排序；按住拖动可调整字段顺序'
          : '按住左右拖动调整字段顺序'}
      onPointerDown={(event) => handleColumnPointerDown(event, column)}
    >
      {column.locked ? column.label : <>
        <span className="sku-column-label">
          {column.label}
          {headerSortable && (
            <span className={`sku-column-sort-icon${sortActive ? ' is-active' : ''}`} aria-hidden="true">
              {!sortActive
                ? <ChevronsUpDown />
                : sort.direction === 'asc' ? <ArrowUp /> : <ArrowDown />}
            </span>
          )}
        </span>
        <span
          className="sku-column-resizer"
          role="separator"
          aria-label={`调整${column.label}列宽`}
          onPointerDown={(event) => {
            event.preventDefault()
            event.stopPropagation()
            resizeRef.current = { id: column.id, startX: event.clientX, startWidth: columnWidths[column.id] || column.width }
            document.body.classList.add('is-resizing-column')
          }}
        />
      </>}
    </th>
  })
  const handleTableScroll = (left: number) => {
    setTableScrollLeft(left)
    const docked = dockedHorizontalScrollRef.current
    if (docked && Math.abs(docked.scrollLeft - left) > 0.5) docked.scrollLeft = left
  }
  const handleDockedHorizontalScroll = (left: number) => {
    setTableScrollLeft(left)
    const source = tableScrollRef.current
    if (source && Math.abs(source.scrollLeft - left) > 0.5) source.scrollLeft = left
  }

  return <div ref={workspaceRef} className="sku-master-workspace unified-table-workbench">
    <section ref={toolbarRef} className="product-master-toolbar sku-master-sticky-toolbar">
      <section ref={savedViewBarRef} className="product-master-saved-view-bar" aria-label="页面管理">
        <div className="product-master-saved-view-tabs" role="tablist" aria-label="已保存页面">
          <button type="button" role="tab" aria-selected={activeViewId === 'default'} className={activeViewId === 'default' ? 'is-active' : ''} onClick={() => switchView('default')}><Table2 aria-hidden="true" />全部数据</button>
          {savedViews.map((view) => <span key={view.id} className={`product-master-saved-view-tab${activeViewId === view.id ? ' is-active' : ''}`}>
            <button type="button" role="tab" aria-selected={activeViewId === view.id} onClick={() => switchView(view.id)}><Table2 aria-hidden="true" />{view.name}{view.locked && <Lock className="product-master-view-lock" aria-label="已锁定" />}</button>
            <button type="button" className="product-master-saved-view-more" aria-label={`页面设置 ${view.name}`} onClick={() => setOpenViewMenuId((current) => current === view.id ? '' : view.id)}><MoreVertical aria-hidden="true" /></button>
          </span>)}
          <button type="button" className="product-master-new-view" onClick={() => { setCreateViewOpen(true); setNewViewName(''); setOpenViewMenuId('') }}><Plus aria-hidden="true" />新建页面</button>
        </div>
        {openView && <div className="product-master-saved-view-menu unified-view-menu" role="menu">
          <button type="button" disabled={openView.locked} onClick={() => { setRenameViewId(openView.id); setRenameViewName(openView.name); setOpenViewMenuId('') }}><Pencil aria-hidden="true" />重命名</button>
          <button type="button" onClick={() => toggleViewLock(openView)}>{openView.locked ? <LockOpen aria-hidden="true" /> : <Lock aria-hidden="true" />}{openView.locked ? '解锁视图' : '锁定视图'}</button>
          <button type="button" onClick={() => duplicateView(openView)}><Copy aria-hidden="true" />复制视图</button>
          <div className="product-master-saved-view-menu-divider" />
          <button type="button" className="is-danger" disabled={openView.locked} onClick={() => { setDeleteViewId(openView.id); setOpenViewMenuId('') }}><Trash2 aria-hidden="true" />删除视图</button>
        </div>}
        {createViewOpen && <form className="product-master-create-view" onSubmit={createSavedView}><input autoFocus aria-label="新页面名称" value={newViewName} maxLength={24} placeholder="输入页面名称" onChange={(event) => setNewViewName(event.target.value)} /><button type="submit">创建</button><button type="button" onClick={() => setCreateViewOpen(false)}>取消</button></form>}
        {renameViewId && <form className="product-master-create-view" onSubmit={renameSavedView}><input autoFocus aria-label="重命名页面" value={renameViewName} maxLength={24} onChange={(event) => setRenameViewName(event.target.value)} /><button type="submit">确认</button><button type="button" onClick={() => setRenameViewId('')}>取消</button></form>}
        {deleteViewId && <div className="product-master-delete-view-confirm"><span>确认删除这个页面？</span><button type="button" className="is-danger" onClick={deleteView}>确认删除</button><button type="button" onClick={() => setDeleteViewId('')}>取消</button></div>}
        {!createViewOpen && !renameViewId && !deleteViewId && viewDirty && <div className="product-master-view-dirty"><span>视图配置已调整</span><button type="button" onClick={() => applyView(activeConfig)}>还原</button><button type="button" className="is-primary" onClick={saveActiveView}><Save aria-hidden="true" />保存</button>{activeViewId === 'default' && <button type="button" onClick={() => { setCreateViewOpen(true); setNewViewName('') }}>另存为新页面</button>}</div>}
        {viewMessage && <span className="product-master-view-message">{viewMessage}</span>}
      </section>
      <div className="product-master-actions">
        <div ref={fieldControlRef} className="product-master-field-control"><button type="button" className={fieldPanelOpen ? 'is-popover-open' : ''} onClick={() => { setFieldPanelOpen(!fieldPanelOpen); setFilterPanelOpen(false); setSortPanelOpen(false); closeFilterValueOptions() }}><Columns3 aria-hidden="true" />字段显示 <span className="product-master-field-count">{visibleColumns.length}/{columns.length}</span></button>
          {fieldPanelOpen && <div className="product-master-field-panel" role="dialog" aria-label="字段显示设置"><div className="product-master-field-panel-header"><strong>字段显示</strong><span>已显示 {visibleColumns.length}/{columns.length}</span></div><div className="product-master-field-list">{orderedColumns.map((column) => {
            const shown = !hiddenColumns.has(column.id)
            return <button key={column.id} type="button" className={`product-master-field-row${shown ? '' : ' is-hidden'}${column.locked ? ' is-locked' : ''}`} disabled={column.locked} onClick={() => toggleColumn(column)}><span>{column.label}</span><span>{column.locked && <small>固定</small>}{shown ? <Eye aria-hidden="true" /> : <EyeOff aria-hidden="true" />}</span></button>
          })}</div><div className="product-master-field-panel-footer"><button type="button" onClick={() => setHiddenColumns(new Set())}>全部显示</button><button type="button" onClick={() => { setHiddenColumns(new Set()); setColumnWidths({}); setColumnOrder([]) }}><RotateCcw aria-hidden="true" />恢复默认</button></div></div>}
        </div>
        <div ref={filterControlRef} className="product-master-filter-control"><button type="button" className={`${filterPanelOpen ? 'is-popover-open ' : ''}${filters.length ? 'has-active-filters' : ''}`} onClick={() => { setFilterPanelOpen(!filterPanelOpen); setFieldPanelOpen(false); setSortPanelOpen(false); closeFilterValueOptions() }}><ListFilter aria-hidden="true" />筛选{filters.length > 0 && <span className="product-master-filter-count">{filters.length}</span>}</button>
          {filterPanelOpen && <div className="product-master-filter-panel" role="dialog" aria-label="筛选条件"><div className="product-master-filter-header"><div><strong>设置筛选条件</strong><span>多个条件同时满足</span></div>{filters.length > 0 && <button type="button" onClick={() => { closeFilterValueOptions(); setFilters([]) }}>清空</button>}</div><div className={`product-master-filter-list${openFilterOptionsId !== null ? ' has-open-options' : ''}`}>{filters.map((filter, index) => {
            const column = columnById.get(filter.field as ColumnId) || columns[0]
            const type = column?.filterType || 'text'
            const valueOptions = type === 'text' ? (filterValueOptions[filter.field] || []) : []
            const useValuePicker = filterNeedsValue(filter.operator) && valueOptions.length > 0
            const query = filterValueQuery.trim().toLowerCase()
            const visibleOptions = query
              ? valueOptions.filter((option) => option.toLowerCase().includes(query))
              : valueOptions
            return (
              <div className="product-master-filter-row" key={filter.id}>
                <span className="product-master-filter-join">{index === 0 ? '当' : '且'}</span>
                <select
                  value={filter.field}
                  onChange={(event) => {
                    const next = columnById.get(event.target.value as ColumnId)
                    const nextType = next?.filterType || 'text'
                    closeFilterValueOptions()
                    updateFilter(filter.id, { field: event.target.value, operator: FILTER_OPERATORS[nextType][0].value, value: '' })
                  }}
                >
                  {columns.filter((item) => item.filterable !== false).map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                </select>
                <select
                  value={filter.operator}
                  onChange={(event) => {
                    closeFilterValueOptions()
                    updateFilter(filter.id, { operator: event.target.value as FilterOperator })
                  }}
                >
                  {FILTER_OPERATORS[type].map((operator) => <option key={operator.value} value={operator.value}>{operator.label}</option>)}
                </select>
                {!filterNeedsValue(filter.operator) ? (
                  <input disabled placeholder="无需输入" />
                ) : useValuePicker ? (
                  <div className="product-master-filter-value-picker">
                    <button
                      type="button"
                      className={`product-master-filter-value-trigger${filter.value ? '' : ' is-placeholder'}`}
                      aria-label={`筛选值 ${index + 1}`}
                      aria-expanded={openFilterOptionsId === filter.id}
                      onClick={() => toggleFilterValueOptions(filter.id)}
                    >
                      <span>{filter.value || '请选择'}</span>
                      <ChevronDown aria-hidden="true" />
                    </button>
                    {openFilterOptionsId === filter.id && (
                      <div className="product-master-filter-value-menu" role="group" aria-label={`筛选值选项 ${index + 1}`}>
                        <div className="product-master-filter-value-search">
                          <Search aria-hidden="true" />
                          <input
                            autoFocus
                            aria-label={`搜索筛选值 ${index + 1}`}
                            value={filterValueQuery}
                            placeholder="搜索选项"
                            onChange={(event) => setFilterValueQuery(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === 'Escape') {
                                event.stopPropagation()
                                closeFilterValueOptions()
                              }
                            }}
                          />
                        </div>
                        <div className="product-master-filter-value-options">
                          <button
                            type="button"
                            className={`product-master-filter-value-option${!filter.value ? ' is-selected' : ''}`}
                            onClick={() => {
                              updateFilter(filter.id, { value: '' })
                              closeFilterValueOptions()
                            }}
                          >
                            请选择
                          </button>
                          {visibleOptions.map((option) => (
                            <button
                              type="button"
                              key={option}
                              className={`product-master-filter-value-option${filter.value === option ? ' is-selected' : ''}`}
                              onClick={() => {
                                updateFilter(filter.id, { value: option })
                                closeFilterValueOptions()
                              }}
                            >
                              {option}
                            </button>
                          ))}
                          {visibleOptions.length === 0 && <p className="product-master-filter-value-empty">没有匹配的选项</p>}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <input
                    type={type === 'number' ? 'number' : type === 'date' ? 'date' : 'text'}
                    value={filter.value}
                    placeholder="请输入"
                    onChange={(event) => updateFilter(filter.id, { value: event.target.value })}
                  />
                )}
                <button
                  type="button"
                  className="product-master-filter-remove"
                  aria-label={`删除筛选条件 ${index + 1}`}
                  onClick={() => {
                    if (openFilterOptionsId === filter.id) closeFilterValueOptions()
                    setFilters((current) => current.filter((item) => item.id !== filter.id))
                  }}
                >
                  <X aria-hidden="true" />
                </button>
              </div>
            )
          })}{!filters.length && <p className="product-master-filter-empty">尚未添加筛选条件。</p>}</div><div className="product-master-filter-footer"><button type="button" onClick={addFilter}><Plus aria-hidden="true" />添加条件</button><span>命中 {processedRows.length.toLocaleString('zh-CN')} 条</span></div></div>}
        </div>
        <div ref={sortControlRef} className="product-master-sort-control"><button type="button" className={`${sortPanelOpen ? 'is-popover-open ' : ''}${sort ? 'has-active-sort' : ''}`} onClick={() => { setSortPanelOpen(!sortPanelOpen); setFieldPanelOpen(false); setFilterPanelOpen(false); closeFilterValueOptions() }}><ArrowUpDown aria-hidden="true" />排序{sort && <span className="product-master-filter-count">1</span>}</button>
          {sortPanelOpen && <div className="product-master-sort-panel" role="dialog" aria-label="排序设置"><div className="product-master-sort-header"><strong>设置排序</strong><span>当前支持单字段排序</span></div><div className="product-master-sort-fields"><label><span>字段</span><select value={sort?.field || columns.find((column) => column.sortable !== false)?.id || ''} onChange={(event) => setSort({ field: event.target.value, direction: sort?.direction || 'asc' })}>{columns.filter((column) => column.sortable !== false).map((column) => <option key={column.id} value={column.id}>{column.label}</option>)}</select></label><label><span>顺序</span><select value={sort?.direction || 'asc'} onChange={(event) => setSort({ field: sort?.field || columns.find((column) => column.sortable !== false)?.id || '', direction: event.target.value as 'asc' | 'desc' })}><option value="asc">升序</option><option value="desc">降序</option></select></label></div><div className="product-master-sort-footer"><span>{sort ? `${columnById.get(sort.field as ColumnId)?.label} · ${sort.direction === 'asc' ? '升序' : '降序'}` : '尚未设置排序'}</span><button type="button" disabled={!sort} onClick={() => setSort(null)}>清除排序</button></div></div>}
        </div>
        <button type="button" title="全部可见列一键按表头文字宽度收紧" onClick={() => fitColumns('header')}><ChevronsRightLeft aria-hidden="true" />适应表头</button>
        <button type="button" title="全部可见列一键按当前内容宽度调整" onClick={() => fitColumns('content')}><ChevronsLeftRight aria-hidden="true" />适应内容</button>
        {editAction}
      </div>
      <label className="search-control"><span className="sr-only">搜索表格</span><Search aria-hidden="true" /><input
        value={search}
        placeholder={searchPlaceholder}
        onCompositionStart={() => { searchCompositionRef.current = true }}
        onCompositionEnd={(event) => {
          searchCompositionRef.current = false
          setSearch(event.currentTarget.value)
          setCommittedSearch(event.currentTarget.value)
          setPage(1)
        }}
        onChange={(event) => {
          const value = event.target.value
          setSearch(value)
          if (!searchCompositionRef.current && !(event.nativeEvent as InputEvent).isComposing) setCommittedSearch(value)
          setPage(1)
        }}
      /></label>
    </section>
    <div className={`sku-master-table-shell${continuous ? ' unified-continuous-table-shell' : ''}${horizontalDock.visible ? ' has-docked-horizontal-scroll' : ''}`}>
      {horizontalDock.visible && <div
        ref={dockedHorizontalScrollRef}
        className="sku-master-docked-horizontal-scroll"
        style={{ left: `${horizontalDock.left}px`, width: `${horizontalDock.width}px` }}
        aria-label="表格横向滚动"
        tabIndex={0}
        onScroll={(event) => handleDockedHorizontalScroll(event.currentTarget.scrollLeft)}
      ><div style={{ width: `${tableWidth}px` }} /></div>}
      {continuous && summary && summaryDock.visible && <div className="sku-master-docked-summary" style={{ left: `${summaryDock.left}px`, width: `${summaryDock.width}px` }}>
        <table className={`product-master-table unified-workbench-table ${tableClassName}`} aria-label="固定底部统计" style={{ minWidth: `${tableWidth}px`, width: `${tableWidth}px`, tableLayout: 'fixed', transform: `translateX(-${tableScrollLeft}px)` }}>
          <colgroup>{visibleColumns.map((column) => <col key={column.id} style={{ width: `${columnWidths[column.id] || column.width}px` }} />)}</colgroup>
          <tfoot className="sku-master-summary-foot"><tr>{renderSummaryCells('docked')}</tr></tfoot>
        </table>
      </div>}
      <div className="sku-master-sticky-header" style={{ top: `${stickyToolbarTop + stickyToolbarHeight}px` }}>
        <table className={`product-master-table unified-workbench-table ${tableClassName}`} aria-label="固定表头" style={{ minWidth: `${tableWidth}px`, width: `${tableWidth}px`, tableLayout: 'fixed', transform: `translateX(-${tableScrollLeft}px)` }}>
          <colgroup>{visibleColumns.map((column) => <col key={column.id} style={{ width: `${columnWidths[column.id] || column.width}px` }} />)}</colgroup>
          <thead><tr>{renderHeaderCells()}</tr></thead>
        </table>
      </div>
    <div ref={tableScrollRef} className="table-responsive product-master-table-wrap unified-table-wrap" onScroll={(event) => handleTableScroll(event.currentTarget.scrollLeft)}>
      <table className={`product-master-table unified-workbench-table ${tableClassName}`} style={{ minWidth: `${tableWidth}px`, width: `${tableWidth}px`, tableLayout: 'fixed' }}>
        <colgroup>{visibleColumns.map((column) => <col key={column.id} style={{ width: `${columnWidths[column.id] || column.width}px` }} />)}</colgroup>
        <thead className="sku-master-source-head"><tr>{renderHeaderCells()}</tr></thead>
        <tbody>
          {virtualTopHeight > 0 && <tr className="unified-virtual-spacer" aria-hidden="true"><td colSpan={Math.max(1, visibleColumns.length)} style={{ height: `${virtualTopHeight}px` }} /></tr>}
          {renderedRows.map((row, rowIndex) => <tr key={rowKey(row)}>{visibleColumns.map((column) => <td key={column.id} className={`${column.id === 'rowNumber' ? 'sku-row-number ' : ''}workbench-column-${column.id}`}>{column.render(row, { rowNumber: continuous ? virtualStart + rowIndex + 1 : (currentPage - 1) * pageSize + rowIndex + 1 })}</td>)}</tr>)}
          {virtualBottomHeight > 0 && <tr className="unified-virtual-spacer" aria-hidden="true"><td colSpan={Math.max(1, visibleColumns.length)} style={{ height: `${virtualBottomHeight}px` }} /></tr>}
          {!renderedRows.length && <tr><td colSpan={Math.max(1, visibleColumns.length)}><div className="empty-state">{emptyMessage}</div></td></tr>}
        </tbody>
        {summary && <tfoot className="sku-master-summary-foot" aria-hidden={summaryDock.visible}><tr>{renderSummaryCells('source')}</tr></tfoot>}
      </table>
    </div>
    </div>
    <div className="product-master-status"><span>{continuous ? `全部 ${processedRows.length.toLocaleString('zh-CN')} 条 · 连续滚动，无分页` : `已筛选 ${processedRows.length.toLocaleString('zh-CN')} 条记录`}</span>{statusNote}</div>
    {!continuous && <Pagination page={currentPage} pageCount={pageCount} total={processedRows.length} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1) }} />}
  </div>
}
